import classNames from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { setupForDark } from '@theme';
import { useWindowSize } from '@uidotdev/usehooks';

const ContextMenu = ({
  children,
  onClose = () => {},
  withBackground = true,
  withBackdrop = true,
  position,
  className = setupForDark('bg-secondary')
}: {
  children: ReactNode;
  onClose?: () => void;
  withBackground?: boolean;
  withBackdrop?: boolean;
  position?: { x: number; y: number };
  className?: string;
}) => {
  const [rect, setRect] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const windowSize = useWindowSize();
  const contextMenuRef = useRef(null);
  const [menuCoords, setMenuCoords] = useState({ ...position });
  const [windowState, setWindowState] = useState({ width: null, height: null });
  useEffect(() => {
    if (position) {
      setMenuCoords({ ...position });
    }
  }, [position]);

  useEffect(() => {
    if (contextMenuRef.current) {
      const resizeObserver = new ResizeObserver((entries) => {
        // we are attaching only single ref which is the size of the context menu
        let rect = entries[0].contentRect;
        setRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
      });
      resizeObserver.observe(contextMenuRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [contextMenuRef]);

  useEffect(() => {
    if (windowSize.width && windowSize.height) {
      if (windowState.width === null && windowState.height === null) {
        setWindowState({ width: windowSize.width, height: windowSize.height });
      } else {
        if (windowState.width !== windowSize.width || windowState.height !== windowSize.height) {
          // We want to close the opened context menu if the user changes the size of the window
          onClose();
        }
      }
    }
    if (menuCoords && rect && windowSize.width && windowSize.height) {
      let coordX = menuCoords.x;
      let coordY = menuCoords.y;
      if (menuCoords.x + rect.width > windowSize.width) {
        coordX = windowSize.width - rect.width;
      }
      if (menuCoords.y + rect.height > windowSize.height) {
        coordY = windowSize.height - rect.height;
      }
      setMenuCoords({ x: coordX, y: coordY });
    }
  }, [position, rect, windowSize.width, windowSize.height]);

  const backdropBg = withBackground ? 'bg-black' : '';

  return (
    <div>
      {/* eslint-disable react/no-unknown-property */}
      <style jsx global>
        {`
          body {
            overflow: hidden;
          }
        `}
      </style>
      <div
        className={classNames('z-50 block fixed dk:min-w-max')}
        style={{
          top: menuCoords?.y,
          left: menuCoords?.x
        }}
      >
        <div
          ref={contextMenuRef}
          className={classNames(
            'rounded-sm transition-all shadow-[7px_9px_33px_-6px_rgba(0,0,0,1)] overflow-hidden',
            className
          )}
        >
          {children}
        </div>
      </div>
      {withBackdrop && (
        <div
          className={`fixed inset-0 w-dvw h-dvh z-40 transition-opacity opacity-70 ${backdropBg}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
        ></div>
      )}
    </div>
  );
};

export default ContextMenu;
